/**
 * state/chat/types.ts — the chat layer's shapes and constants.
 *
 * No store, no side effects: every other module in the package may
 * import this one, and nothing here may import them back.
 */

import type { TurnProgress } from '~/lib/turn-progress';

import type { ChatConvSummary, ChatQueueItem } from '~/lib/daemon-client';
import type { ChatAttachment } from '~/lib/chat-sanitize';
import type { DelegationCard } from '~/lib/delegation';

export type { ChatAttachment };

export const ONBOARDING_CONV_ID = '_onboarding_v1';

/** The fixed/system agent present in every project: the Architect
 *  (`_onboarding_v1`). It cannot be archived, reordered, or removed from
 *  the rail's pinned head — see ChatRail's head/body split and
 *  AgentCard's draggable guard. BT-19 — the Orchestrator
 *  (`roadmap-architect-*`) is retired; its old convs are ordinary,
 *  archivable history. */
export function isFixedAgentConv(conv: string | null | undefined): boolean {
  return conv === ONBOARDING_CONV_ID;
}

export type AgentType =
  | 'custom' | 'deploy' | 'db' | 'testing' | 'audit' | 'docs' | 'review' | 'roadmap-architect';

export interface ConvMeta {
  agentId: string;
  model: string;
  /** MP3 (2026-06-12) — reasoning-depth dial → claude-code `--effort`.
   *  'default' / undefined = no flag. */
  effort?: string;
  /** DM-CLI-02 (multi-cli-clients) — which CLI dispatches this conv's
   *  turns. undefined/'claude-code' = the default. */
  client?: string;
  /** MPV1 — the LLM backend for claude-code turns, mirrored from the
   *  daemon sidecar (member save stamps it, `conv.meta_updated` carries
   *  it). Display-only: the header needs it to pick the right model
   *  catalog; dispatches resolve provider daemon-side from the member. */
  provider?: string;
  /** ES-2 — the operator picked `client`/`provider` for THIS conv in the
   *  chat header. While set, the header shows it over the member card and
   *  every dispatch sends it with `pin_engine`; the daemon keeps it until
   *  the member card's engine is saved again (which clears this flag via
   *  `conv.meta_updated`). */
  enginePinned?: boolean;
  type: AgentType;
  title: string;
  /** agent-team (ATM7/ATM10) — the roster member this conv is bound to
   *  (`developer`, `api-developer`, …). Set at creation from the picker
   *  and frozen after the first dispatch. Passed as `member` on every
   *  dispatch so the daemon loads that member's init prompt + refs. */
  member?: string;
  location: { type: 'local' | 'remote'; host?: string; provider?: string };
}

/** py-1.11.0 — Status kind used by the rail's `AgentCard` prop. */
export type AgentStatusKind = 'idle' | 'thinking' | 'working';

export interface ChatMsg {
  /**
   * 'user'      — operator-typed message (and its echoes from the daemon).
   * 'assistant' — agent reply (deltas + final).
   * 'system'    — in-band notice: dispatch errors (client-only), a turn
   *               that died (`chat.error`, persisted) and, since ES-2, the
   *               "new code client from here" divider (`chat.notice`).
   */
  kind: 'user' | 'assistant' | 'system';
  text: string;
  /** ENG1/CCA4 — the code client that produced an assistant reply (finals
   *  carry it since py-1.42.6). Absent on older replies. */
  client?: string;
  author?: string;
  ts?: string;
  streaming?: boolean;
  stream_id?: string;
  cancelled?: boolean;
  /** Severity styling for client-side 'system' messages. */
  system_kind?: 'error' | 'warning' | 'info' | 'divider' | 'delegation';
  /** BT-17 — for `system_kind: 'delegation'`: the card built from the
   *  `delegate.*` events of one request_id, updated in place. */
  delegation?: DelegationCard;
  /** REL1 — for a `system`/error bubble born from a failed dispatch: the
   *  text that never made it, so the thread can offer "resend" instead of
   *  making the operator retype it. */
  retry_text?: string;
  /** py-1.12.21 — attachments persisted by the daemon. Each entry's
   *  `url` resolves to `GET /chat/uploads/<bucket>/<file>`. Emitted
   *  only for `kind: 'user'` events whose dispatch carried images/docs. */
  attachments?: ChatAttachment[];
  _placeholder_user?: boolean;
  _placeholder?: boolean;
}

/** MP5 — per-cluster activity indicators surfaced on the projects rail.
 *  Tracked GLOBALLY (across all clusters), not swapped on bindCluster,
 *  because we want to know "B is working" while we're on A. */
export interface ClusterActivity {
  /** Wall-clock ts of the last event received on this cluster's WS. */
  lastEventAt: number;
  /** Wall-clock ts when the cockpit last bound to this cluster. */
  lastReadAt: number;
  /** Convs currently streaming an assistant reply on this cluster. */
  workingConvs: string[];
}

export interface ChatPaging {
  /** Daemon says there are older messages beyond what we've loaded. */
  hasMore: boolean;
  /** ISO ts of the oldest message currently in convMap (the `before`
   *  cursor for the next older page). */
  oldestTs: string;
  /** A page fetch is in flight (prevents double-trigger on scroll). */
  loading: boolean;
}

export interface ChatStoreState {
  convMap: Record<string, ChatMsg[]>;
  activeConv: string | null;
  archivedConvs: Record<string, true>;
  convMeta: Record<string, ConvMeta>;
  convTitleOverrides: Record<string, string>;
  /** MP5 — global per-cluster activity. NOT swapped on bindCluster. */
  clusterActivity: Record<string, ClusterActivity>;
  /** V86p — convs the operator just dispatched into, awaiting the first
   *  assistant chunk over WS. Carries the dispatch timestamp so the UI
   *  can show "preparing… Ns elapsed". Cleared on the first
   *  delta / final / cancelled. */
  pendingReplyConvs: Record<string, number>;
  /** V89.2 — wall-clock ts of the most recent `chat.assistant.delta`
   *  per conv, so the streaming bubble can tell when the agent has been
   *  quiet "too long" (>~1.5 s). Cleared on final/cancelled. */
  lastDeltaTsByConv: Record<string, number>;
  /** LT-3 — the daemon's live `conv.progress` per working conv: what the
   *  turn is doing right now (tool / streaming / waiting), how long it
   *  has run, how long it has been quiet. Replaces the rotating pool of
   *  invented verbs that used to stand in for this. Cleared when the
   *  turn ends. */
  turnProgress: Record<string, TurnProgress>;
  /** py-1.11.0 — daemon-authoritative conv summaries from
   *  `GET /chat/snapshot` + WS conv.* events. When populated this is the
   *  single source of truth for the rail list AND for "is this conv live
   *  / coordinating / waiting on who". Empty `{}` when the daemon lacks
   *  `chat.snapshot.v1`. */
  convs: Record<string, ChatConvSummary>;
  /** ISO ts of the last full snapshot hydration. Null before the first
   *  hydration of a cluster the cockpit has never seen this session. */
  convsHydratedAt: string | null;
  /** AX3 — `convs`/`convsHydratedAt` were restored from the in-memory
   *  cluster cache on switch-back and have NOT been revalidated yet.
   *  The workspace paints from them immediately (that is the point) but
   *  surfaces a "refreshing…" marker; `hydrateFromSnapshot` clears the
   *  flag, and fresh daemon data always wins over the cached copy. */
  convsStale: boolean;
  /** V107.41 — Standard v16 chat-turn queue. Per-conv list of items
   *  waiting to be dispatched. Daemon-authoritative. */
  queues: Record<string, ChatQueueItem[]>;
  /** LAL9 — convs the daemon flagged with `conv.anchor_missing` (conv →
   *  ISO ts). Set when the agent skipped its `⟦anchor⟧` marker; cleared
   *  on the next `conv.anchored` or when the conv goes idle. Lets the
   *  queue bar warn that the WORKING highlight may point at a stale
   *  initiative instead of failing silent. */
  anchorMissing: Record<string, string>;
  /** 2026-06-12 — per-conv pagination cursor for the windowed history
   *  loader. Storage keeps everything; we just don't paint past the cap. */
  paging: Record<string, ChatPaging>;
}

/** The per-cluster chat slice cached in memory across a project switch.
 *  AX3 added `convs`/`convsHydratedAt` so switch-back paints instantly
 *  instead of blocking on two serial round-trips. */
export interface ClusterChatSlice {
  convMap: Record<string, ChatMsg[]>;
  activeConv: string | null;
  archivedConvs: Record<string, true>;
  convMeta: Record<string, ConvMeta>;
  convTitleOverrides: Record<string, string>;
  convs: Record<string, ChatConvSummary>;
  convsHydratedAt: string | null;
  /** QISO1 — conv ids (`_onboarding_v1`) repeat in every project, so
   *  anything keyed by conv alone must travel with its project. */
  queues: Record<string, ChatQueueItem[]>;
  paging: Record<string, ChatPaging>;
}

export const initialChatState: ChatStoreState = {
  convMap: {},
  activeConv: null,
  archivedConvs: {},
  convMeta: {},
  convTitleOverrides: {},
  clusterActivity: {},
  pendingReplyConvs: {},
  lastDeltaTsByConv: {},
  turnProgress: {},
  convs: {},
  convsHydratedAt: null,
  convsStale: false,
  queues: {},
  anchorMissing: {},
  paging: {},
};

// 2026-06-12 — windowed history loader knobs.
//   INITIAL_PAGE — messages loaded when a conv gains focus / on reload.
//   PAGE         — messages loaded per scroll-up step.
// PAGE1 (2026-10-04) — no ceiling: scrolling up keeps loading until the
// conversation's first message, and what is loaded stays in memory.
export const INITIAL_PAGE = 20;
export const PAGE = 10;

/**
 * V107.8 — Infer agent_type from the conv slug pattern.
 *
 * The slug is the unforgeable signal of intent — every other channel
 * (meta.type in localStorage, body.agent_type in the dispatch, the
 * daemon's conv_meta sidecar) can drift out of sync. Sister of the
 * daemon's `_agent_type_from_conv_slug` (py-1.10.12).
 */
const SLUG_TYPE_PREFIXES: ReadonlyArray<readonly [string, AgentType]> = [
  ['roadmap-architect-', 'roadmap-architect'],
  ['deploy-', 'deploy'],
  ['db-', 'db'],
  ['testing-', 'testing'],
  ['audit-', 'audit'],
  ['docs-', 'docs'],
  ['review-', 'review'],
];

export function agentTypeFromSlug(conv: string): AgentType | null {
  if (!conv) return null;
  for (const [prefix, implied] of SLUG_TYPE_PREFIXES) {
    if (conv.startsWith(prefix)) return implied;
  }
  return null;
}

/** Agent types whose chat renders as one continuous autonomous run. */
export const AUTONOMOUS_AGENT_TYPES: ReadonlySet<AgentType> = new Set<AgentType>(['roadmap-architect']);

export interface DispatchOpts {
  conv: string;
  text: string;
  author?: string;
  images?: Array<{ dataURL: string; mediaType: string }>;
  contextDocs?: Array<{ filename: string; content: string }>;
  scope?: { module?: string; taskId?: string; initiative?: string };
}

export type DispatchOutcome =
  | { ok: true; conv: string }
  | { ok: false; status: number; error?: string };
